import React from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Image from '../../../AppImage';

const StartupCard = ({ startup, onViewProfile, onContact, userRole = 'corporate' }) => {
  const getStageColor = (stage) => {
    switch (stage) {
      case 'pre-seed': return 'text-blue-600 bg-blue-50';
      case 'seed': return 'text-green-600 bg-green-50';
      case 'series-a': return 'text-purple-600 bg-purple-50';
      case 'series-b': return 'text-orange-600 bg-orange-50';
      case 'series-c': return 'text-red-600 bg-red-50';
      case 'growth': return 'text-indigo-600 bg-indigo-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getMatchScore = (score) => {
    if (score >= 90) return { color: 'text-success', bg: 'bg-success/10', label: 'Excellent Match' };
    if (score >= 75) return { color: 'text-primary', bg: 'bg-primary/10', label: 'Good Match' };
    if (score >= 60) return { color: 'text-warning', bg: 'bg-warning/10', label: 'Fair Match' };
    return { color: 'text-muted-foreground', bg: 'bg-muted', label: 'Low Match' };
  };

  const matchInfo = getMatchScore(startup?.matchScore);

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
            <Image
              src={startup?.logo}
              alt={startup?.logoAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">{startup?.name}</h3>
            <p className="text-sm text-muted-foreground">{startup?.tagline}</p>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(startup?.stage)}`}>
            {startup?.stage?.replace('-', ' ')?.toUpperCase()}
          </span>
          {startup?.matchScore && (
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${matchInfo?.color} ${matchInfo?.bg}`}>
              {startup?.matchScore}% Match
            </div>
          )}
        </div>
      </div>
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {startup?.description}
      </p>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="MapPin" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{startup?.location}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">Founded {startup?.founded}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{startup?.teamSize} employees</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="DollarSign" size={14} className="text-muted-foreground" />
          <span className="text-sm text-foreground">{startup?.funding} raised</span>
        </div>
      </div>
      {/* Technologies */}
      <div className="flex flex-wrap gap-2 mb-4">
        {startup?.technologies?.slice(0, 3)?.map((tech, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
          >
            {tech}
          </span>
        ))}
        {startup?.technologies?.length > 3 && (
          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
            +{startup?.technologies?.length - 3} more
          </span>
        )}
      </div>
      {/* Key Achievements */}
      {startup?.achievements && startup?.achievements?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Key Achievements:</p>
          <div className="space-y-1">
            {startup?.achievements?.slice(0, 2)?.map((achievement, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Award" size={12} className="text-primary" />
                <span className="text-xs text-foreground">{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Match Reasons (if available) */}
      {startup?.matchReasons && startup?.matchReasons?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">Why this matches:</p>
          <div className="space-y-1">
            {startup?.matchReasons?.slice(0, 2)?.map((reason, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={12} className="text-success" />
                <span className="text-xs text-foreground">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Icon name="TrendingUp" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{startup?.growth}% growth</span>
          </div>
          {startup?.verified && (
            <div className="flex items-center space-x-1">
              <Icon name="Shield" size={14} className="text-success" />
              <span className="text-xs text-success">Verified</span>
            </div>
          )}
          {startup?.featured && (
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={14} className="text-warning" />
              <span className="text-xs text-warning">Featured</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProfile(startup)}
            iconName="Eye"
            iconPosition="left"
            iconSize={14}
          >
            Profile
          </Button>
          {userRole === 'corporate' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onContact(startup)}
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={14}
            >
              Contact
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StartupCard;